import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { API_URL, useAuth } from '@/contexts/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Plus, Minus, Trash2, Calculator, Loader2, Search, X, Lock } from 'lucide-react';
import { toast } from 'sonner';
import { Checkbox } from '@/components/ui/checkbox';
import { PageCover, PageBody, SheetHeader, StatusDot, DOT } from '@/components/Page';
import { mesmaOpcaoDeFrete } from '@/lib/frete';
import { formatBRL } from '@/lib/format';
import { AVISO_VENDA_SEM_ESTOQUE, cartaoDeEstoque, faltasDoErro, linhasDeFalta } from '@/lib/estoque';
import ConfirmDialog from '@/components/ConfirmDialog';
import CreditoBcmed from '@/components/CreditoBcmed';
import {
  creditoAplicado, documentoDoCredito, erroDeCredito, itensDoCredito, limiteDoCredito, limitarCredito,
} from '@/lib/credito';
// Formas de pagamento (values = enum FormaPagamento do backend): cartão de crédito (Pagar.me) e Belluno
// geram link e confirmam sozinhos; as demais exigem comprovante. Financiamento: o formulário só oferece na
// proposta; o pedido de financiamento nasce na aprovação e, editado, mantém a forma.
import {
  FINANCIAMENTO, FORMAS_COM_LINK, FORMAS_DO_FORMULARIO, camposDePagamento, nomeDaForma, opcoesDeParcelas,
  resumoFinanciamento,
} from '@/lib/pagamento';

// Duração (ms) do destaque visual numa seção com erro de validação no submit.
const HIGHLIGHT_MS = 2000;

// Os campos já seguem o sistema E (components/ui). Aqui só o autofill: o browser
// pinta fundo branco no tema escuro, e a sombra interna devolve a cor da folha.
const FIELD_CLS = 'dark:autofill:shadow-[inset_0_0_0_1000px_rgb(15_21_32)] dark:autofill:[-webkit-text-fill-color:rgb(237_238_242)]';

// Espelha o backend (routes/pedidos.py::_checkout_gerado): com checkout gerado,
// vendedor e Sales Ops não trocam o endereço de entrega — o backend recusa (409);
// aqui os campos ficam só leitura para ninguém descobrir isso ao salvar.
const temCheckoutGerado = (order) => Boolean(
  order?.checkout_token || order?.payment_link_id || order?.payment_link
  || (order?.pagarme_order_ids || []).length
  || (order?.pagamento_split || []).some((p) => p.checkout_token || p.payment_link_id || p.pagarme_order_id)
);
const TRAVADO_CLS = 'bg-muted text-gray-500 cursor-not-allowed';

// Badge numerado das seções do formulário (discreto, sem cor decorativa).
const StepBadge = ({ n }) => (
  <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-muted text-xs font-semibold tabular-nums text-gray-600 dark:text-gray-300">
    {n}
  </span>
);

const CreateOrder = ({ modo = 'pedido' }) => {
  const navigate = useNavigate();
  const { id } = useParams(); // ID do pedido/proposta para edição
  const isEditMode = Boolean(id);
  // Modo proposta: o MESMO formulário, mas salva em /propostas (com validade)
  // e volta para a lista de propostas. Carrinho, frete e pagamento não mudam.
  const isProposta = modo === 'proposta';
  const [validadeDias, setValidadeDias] = useState(7);
  const { user } = useAuth();
  // Administrativo edita a partir da Gestão de Pedidos (não acessa /pedidos).
  const voltarPara = isProposta ? '/propostas'
    : user?.role === 'administrativo' ? '/gestao-pedidos' : '/pedidos';
  // Sales Ops cria pedido EM NOME de um vendedor: o pedido nasce atribuído a
  // ele (link de pagamento e nome no Tiny saem do vendedor selecionado).
  const isSalesOps = user?.role === 'sales_ops';
  const [checkoutGerado, setCheckoutGerado] = useState(false);
  // Endereço travado depois do checkout: só master/administrativo alteram.
  const enderecoTravado = isEditMode && !isProposta && checkoutGerado
    && !['master', 'administrativo'].includes(user?.role);
  const [vendedores, setVendedores] = useState([]);
  const [vendedorId, setVendedorId] = useState('');

  const [products, setProducts] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingOrder, setLoadingOrder] = useState(false);
  const [calculatingFrete, setCalculatingFrete] = useState(false);
  // Seção destacada (ring + scroll) quando o submit falha por campo obrigatório.
  const [highlightSection, setHighlightSection] = useState(null);

  // Form state
  const [clienteNome, setClienteNome] = useState('');
  const [clienteEmail, setClienteEmail] = useState('');
  const [clienteTelefone, setClienteTelefone] = useState('');
  const [clienteCpfCnpj, setClienteCpfCnpj] = useState('');
  const [cep, setCep] = useState('');
  const [endereco, setEndereco] = useState('');
  const [numero, setNumero] = useState('');
  const [complemento, setComplemento] = useState('');
  const [bairro, setBairro] = useState('');
  const [cidade, setCidade] = useState('');
  const [estado, setEstado] = useState('');
  const [observacoes, setObservacoes] = useState('');
  const [searchingCep, setSearchingCep] = useState(false);

  // Pagamento
  const [formaPagamento, setFormaPagamento] = useState('pix');
  const [parcelamento, setParcelamento] = useState(1);
  const [valorParcela, setValorParcela] = useState(''); // só financiamento: a parcela que a financeira simulou
  // Pedido de financiamento em edição: a forma não muda (a financeira aprovou e paga este pedido) e não pede
  // parcelas (o pedido tem 1; as do cliente ficam em `financiamento`).
  const [financiamentoPedido, setFinanciamentoPedido] = useState(null);
  // Trocar de forma volta as parcelas à menor da forma (1x; na Belluno, 13x): as 24 do financiamento não podem
  // virar o teto do cartão. No financiamento o número começa vazio (um "1" pronto passava na validação e virava
  // "124" ao digitar).
  const trocarFormaPagamento = (forma) => {
    if (!forma) return; // o Select do Radix avisa '' quando o valor some da lista: não é escolha do vendedor
    setFormaPagamento(forma);
    setParcelamento(forma === FINANCIAMENTO ? '' : opcoesDeParcelas(forma)[0]);
    setValorParcela('');
  };

  // Cart state
  const [cartItems, setCartItems] = useState([]);
  // Multi-segmento: cada filial origem tem suas opções de frete.
  // freteSegmentos: [{ empresa_id, filial_label, cep_origem, itens, peso_total_kg, opcoes, erro }]
  // freteSelecionadoPorSegmento: { [empresa_id]: opcao }
  const [freteSegmentos, setFreteSegmentos] = useState([]);
  const [freteSelecionadoPorSegmento, setFreteSelecionadoPorSegmento] = useState({});
  const [retiradaNaLoja, setRetiradaNaLoja] = useState(null);
  const [usarRetiradaNaLoja, setUsarRetiradaNaLoja] = useState(false);
  // Cidade com loja, mas a loja não tem todos os itens: a retirada não é oferecida e a tela diz por quê.
  const [retiradaIndisponivel, setRetiradaIndisponivel] = useState(null);
  const [produtosSemEstoque, setProdutosSemEstoque] = useState([]);
  // Pedido que voltou 409 "sem_estoque": { faltas, orderData } até o vendedor confirmar ou desistir.
  const [semEstoque, setSemEstoque] = useState(null);
  const [freteGratis, setFreteGratis] = useState(false);

  // Crédito BCMED: o valor digitado, o saldo do cliente (consultado com espera quando documento ou produtos mudam),
  // o número do pedido em edição (a reserva dele volta ao disponível) e a trava do pedido já pago.
  const [creditoValor, setCreditoValor] = useState('');
  const [saldoCredito, setSaldoCredito] = useState(null);
  const [creditoIndisponivel, setCreditoIndisponivel] = useState(false);
  const [numeroPedido, setNumeroPedido] = useState(null);
  const [creditoTravado, setCreditoTravado] = useState(false);
  const ultimaConsultaCredito = useRef(0);

  // Product search state
  const [productSearch, setProductSearch] = useState('');
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [showProductDropdown, setShowProductDropdown] = useState(false);

  // Stock state
  const [selectedProductStock, setSelectedProductStock] = useState(null);
  const [loadingStock, setLoadingStock] = useState(false);
  const [productLeadtime, setProductLeadtime] = useState(null);
  // O estoque vem ao vivo do Tiny e pode chegar fora de ordem: só a resposta do último produto buscado vale.
  const ultimaBuscaEstoque = useRef(null);

  // Filial preferida quando o item está em mais de uma origem (apenas
  // marcador visual; o backend define o ranking real via FILIAL_ORIGEM_RANKING).
  const EMPRESA_FRETE_ID = 3;

  const fetchCompanies = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/empresas`);
      setCompanies(response.data);
    } catch {
      // silently fail
    }
  }, []);

  const fetchOrder = useCallback(async () => {
    setLoadingOrder(true);
    try {
      const response = await axios.get(`${API_URL}/${isProposta ? 'propostas' : 'pedidos'}/${id}`);
      // Proposta: o payload do pedido vive em `dados`; a validade é metadado.
      const order = isProposta ? response.data.dados : response.data;
      if (isProposta) setValidadeDias(parseInt(response.data.validade_dias) || 7);
      else setCheckoutGerado(temCheckoutGerado(order));

      setClienteNome(order.cliente_nome || '');
      setClienteEmail(order.cliente_email || '');
      setClienteTelefone(order.cliente_telefone || '');
      setClienteCpfCnpj(order.cliente_cpf_cnpj || '');

      if (order.endereco_entrega) {
        setCep(order.endereco_entrega.cep || '');
        setEndereco(order.endereco_entrega.endereco || '');
        setNumero(order.endereco_entrega.numero || '');
        setComplemento(order.endereco_entrega.complemento || '');
        setBairro(order.endereco_entrega.bairro || '');
        setCidade(order.endereco_entrega.cidade || '');
        setEstado(order.endereco_entrega.estado || '');
      }

      setObservacoes(order.observacoes || '');
      setFormaPagamento(order.forma_pagamento || 'pix');
      setParcelamento(parseInt(order.parcelamento) || 1);
      setValorParcela(order.valor_parcela ? String(order.valor_parcela) : '');
      setCreditoValor(Number(order.credito_bcmed_valor) > 0 ? String(order.credito_bcmed_valor) : '');
      if (!isProposta) {
        setNumeroPedido(order.numero_pedido || null);
        setCreditoTravado(['confirmed', 'refunded'].includes(order.payment_status));
      }
      if (!isProposta && order.forma_pagamento === FINANCIAMENTO) setFinanciamentoPedido(order.financiamento || {});

      const normalizedItems = (order.itens || []).map(item => {
        const preco_original = parseFloat(item.preco_original) || parseFloat(item.preco_unitario) || 0;
        const desconto = parseFloat(item.desconto) || 0;
        const desconto_percentual = preco_original > 0 ? ((desconto / preco_original) * 100).toFixed(2) : 0;

        return {
          ...item,
          quantidade: parseInt(item.quantidade) || 1,
          preco_unitario: parseFloat(item.preco_unitario) || 0,
          preco_original,
          desconto,
          desconto_percentual,
          peso: parseFloat(item.peso) || 1,
          altura: parseFloat(item.altura) || 10,
          largura: parseFloat(item.largura) || 10,
          comprimento: parseFloat(item.comprimento) || 10
        };
      });
      setCartItems(normalizedItems);

      if (order.frete) {
        setFreteGratis(order.frete.gratis || false);

        // Se o pedido foi salvo com segmentos, reidrata como multi-segmento
        if (Array.isArray(order.frete.segmentos) && order.frete.segmentos.length > 0) {
          const segs = order.frete.segmentos.map(seg => ({
            empresa_id: seg.empresa_id,
            filial_label: seg.filial_label,
            cep_origem: seg.cep_origem || '',
            itens: seg.itens || [],
            peso_total_kg: seg.peso_total_kg || 0,
            opcoes: [{
              transportadora: seg.transportadora,
              servico: seg.servico,
              valor: parseFloat(seg.valor) || 0,
              prazo: seg.prazo || seg.prazo_entrega || 0,
              tipo_entrega: seg.tipo_entrega || 'normal',
            }],
            erro: null,
          }));
          setFreteSegmentos(segs);
          const sel = {};
          segs.forEach(s => { sel[s.empresa_id] = s.opcoes[0]; });
          setFreteSelecionadoPorSegmento(sel);
        } else if (order.frete.transportadora === 'Retirar na Loja') {
          setRetiradaNaLoja({
            transportadora: 'Retirar na Loja',
            servico: order.frete.servico || 'Retirada no Local ou Entrega',
            valor: 0,
            prazo: 0,
            tipo_entrega: 'retirada',
            descricao: order.frete.descricao,
            loja_empresa_id: order.frete.loja_empresa_id,
            loja_cidade: order.frete.loja_cidade,
          });
          setUsarRetiradaNaLoja(true);
        } else if (order.frete.transportadora) {
          // Pedido legado single-segmento: reidrata como 1 segmento "qualquer"
          setFreteSegmentos([{
            empresa_id: 0,
            filial_label: 'Origem do pedido',
            cep_origem: '',
            itens: [],
            peso_total_kg: 0,
            opcoes: [{
              transportadora: order.frete.transportadora,
              servico: order.frete.servico || '',
              valor: parseFloat(order.frete.valor) || 0,
              prazo: order.frete.prazo_entrega || 0,
              tipo_entrega: order.frete.tipo_entrega || 'normal',
            }],
            erro: null,
          }]);
          setFreteSelecionadoPorSegmento({ 0: {
            transportadora: order.frete.transportadora,
            servico: order.frete.servico || '',
            valor: parseFloat(order.frete.valor) || 0,
            prazo: order.frete.prazo_entrega || 0,
            tipo_entrega: order.frete.tipo_entrega || 'normal',
          }});
        }
      }

      toast.success(isProposta ? 'Proposta carregada para edição' : 'Pedido carregado para edição');
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error('Sessão expirada. Faça login novamente.');
        navigate('/login');
      } else if (error.response?.status === 403) {
        toast.error('Você não tem permissão para editar este pedido.');
        navigate(voltarPara);
      } else if (error.response?.status === 404) {
        toast.error('Pedido não encontrado.');
        navigate(voltarPara);
      } else {
        toast.error(error.response?.data?.detail || 'Erro ao carregar pedido');
        navigate(voltarPara);
      }
    } finally {
      setLoadingOrder(false);
    }
  }, [id, navigate, isProposta, voltarPara]);

  useEffect(() => {
    fetchCompanies();
    if (isEditMode) {
      fetchOrder();
    }
  }, [isEditMode, fetchCompanies, fetchOrder]);

  useEffect(() => {
    if (!isSalesOps || isEditMode) return;
    axios.get(`${API_URL}/users/vendedores`)
      .then((res) => setVendedores(Array.isArray(res.data) ? res.data : []))
      .catch(() => toast.error('Erro ao carregar a lista de vendedores'));
  }, [isSalesOps, isEditMode]);

  useEffect(() => {
    const searchProducts = async () => {
      if (productSearch.trim().length >= 2) {
        try {
          const response = await axios.get(`${API_URL}/produtos-postgres`, {
            params: { search: productSearch.trim(), limit: 100 },
          });
          setFilteredProducts(response.data);
          setShowProductDropdown(response.data.length > 0);
        } catch {
          toast.error('Erro ao buscar produtos');
          setFilteredProducts([]);
          setShowProductDropdown(false);
        }
      } else {
        setFilteredProducts([]);
        setShowProductDropdown(false);
      }
    };

    const timeoutId = setTimeout(searchProducts, 300);
    return () => clearTimeout(timeoutId);
  }, [productSearch]);

  const searchCep = async (cepValue) => {
    // Remove non-numeric characters
    const cleanCep = cepValue.replace(/\D/g, '');

    // Only search if CEP has 8 digits
    if (cleanCep.length !== 8) {
      return;
    }

    setSearchingCep(true);
    try {
      // fetch e não axios: o axios levaria o token do ERP (header padrão do AuthContext) até o ViaCEP.
      const res = await fetch(`https://viacep.com.br/ws/${cleanCep}/json/`);
      if (!res.ok) throw new Error(`ViaCEP ${res.status}`);
      const data = await res.json();

      if (data.erro) {
        toast.error('CEP não encontrado');
        return;
      }

      // Fill address fields automatically
      setEndereco(data.logradouro || '');
      setBairro(data.bairro || '');
      setCidade(data.localidade || '');
      setEstado(data.uf || '');

      toast.success('CEP encontrado! Endereço preenchido automaticamente');

      // Focus on number field after auto-fill
      setTimeout(() => {
        document.getElementById('numero')?.focus();
      }, 100);
    } catch (error) {
      toast.error('Erro ao buscar CEP. Verifique e tente novamente.');
    } finally {
      setSearchingCep(false);
    }
  };

  const handleCepChange = (e) => {
    const value = e.target.value;
    setCep(value);

    // Trigger search when CEP is complete
    const cleanCep = value.replace(/\D/g, '');
    if (cleanCep.length === 8) {
      searchCep(value);
    }
  };

  const fetchProductStock = async (productSku, productTinyIds) => {
    ultimaBuscaEstoque.current = productSku;
    setLoadingStock(true);
    setProductLeadtime(null);  // Limpar leadtime anterior

    try {
      const response = await axios.get(`${API_URL}/produtos/sku/${productSku}/estoque-realtime`);
      if (ultimaBuscaEstoque.current !== productSku) return;

      // Verificar se há informação de leadtime (produto sem estoque)
      if (response.data.leadtime) {
        setProductLeadtime(response.data.leadtime);
        toast.warning(
          `Produto sem estoque. Prazo do fornecedor: ${response.data.leadtime.prazo_fornecedor}`,
          { duration: 6000 }
        );
      }

      setSelectedProductStock(cartaoDeEstoque(response.data));

      // O que vale é o livre na filial de onde o pedido sai; sem resposta do Tiny, o total da base, como antes.
      const saida = response.data.saida;
      if (saida && saida.disponivel <= 0) {
        toast.warning(`Sem estoque livre na ${saida.filial}: ${saida.saldo} em estoque, ${saida.reservado} já reservados`);
      } else if (saida) {
        toast.success(`${saida.disponivel} livres na ${saida.filial}`);
      } else if (response.data.estoque_total > 0) {
        toast.success(`Estoque carregado: Total ${response.data.estoque_total} unidades`);
      } else {
        toast.info(`Produto sem estoque em todas as filiais`);
      }
    } catch (error) {
      if (ultimaBuscaEstoque.current !== productSku) return;
      toast.error('Erro ao buscar estoque');
      setSelectedProductStock(null);
    } finally {
      if (ultimaBuscaEstoque.current === productSku) setLoadingStock(false);
    }
  };

  const addToCart = (productSku) => {
    // Buscar produto em filteredProducts
    const product = filteredProducts.find(p => p.sku === productSku);
    if (!product) {
      toast.error('Produto não encontrado');
      return;
    }

    // Estoque ao vivo do Tiny (1 ou 2 s): não segura o carrinho; o cartão aparece quando chegar. O carrinho muda
    // sobre o estado mais novo, para um 2º clique durante a espera não sobrescrever o 1º nem duplicar a linha.
    fetchProductStock(product.sku, product.tiny_ids);

    setCartItems((itens) => (itens.some(item => item.sku === productSku)
      ? itens.map(item => (item.sku === productSku ? { ...item, quantidade: item.quantidade + 1 } : item))
      : [
        ...itens,
        {
          produto_id: product.sku, // Usando SKU como ID
          sku: product.sku,
          nome: product.nome,
          quantidade: 1,
          preco_original: product.preco, // Preço original do produto
          preco_unitario: product.preco, // Preço de venda (editável)
          desconto: 0,
          desconto_percentual: 0,
          peso: product.peso || 1,
          altura: product.altura || 10,
          largura: product.largura || 10,
          comprimento: product.comprimento || 10,
          empresa_origem_id: EMPRESA_FRETE_ID // Filial 5 fixa para frete
        }
      ]));
    toast.success(cartItems.some(item => item.sku === productSku)
      ? `Quantidade de ${product.nome} aumentada` : `${product.nome} adicionado ao carrinho`);

    // Limpar busca
    setProductSearch('');
    setShowProductDropdown(false);
  };

  const updatePreco = (index, novoPreco) => {
    const newItems = [...cartItems];
    const preco = parseFloat(novoPreco) || 0;
    newItems[index].preco_unitario = preco;

    // Calcular desconto em valor
    const precoOriginal = parseFloat(newItems[index].preco_original) || 0;
    const descontoValor = Math.max(0, precoOriginal - preco);
    newItems[index].desconto = descontoValor;

    // Calcular a porcentagem do desconto para atualizar o campo
    if (precoOriginal > 0) {
      newItems[index].desconto_percentual = parseFloat(((descontoValor / precoOriginal) * 100).toFixed(2));
    } else {
      newItems[index].desconto_percentual = 0;
    }

    setCartItems(newItems);
  };

  const updateDescontoPercentual = (index, percentual) => {
    const newItems = [...cartItems];
    const perc = parseFloat(percentual) || 0;
    const precoOriginal = parseFloat(newItems[index].preco_original) || 0;

    // Calcula o novo valor de desconto e preço unitário com base na porcentagem
    const descontoValor = precoOriginal * (perc / 100);
    const novoPreco = precoOriginal - descontoValor;

    newItems[index].preco_unitario = Math.max(0, novoPreco);
    newItems[index].desconto = Math.max(0, descontoValor);
    newItems[index].desconto_percentual = percentual === '' ? '' : perc;

    setCartItems(newItems);
  };

  const updateQuantity = (index, quantidade) => {
    if (quantidade < 1) return;
    const newItems = [...cartItems];
    newItems[index].quantidade = quantidade;
    setCartItems(newItems);
  };

  const removeFromCart = (index) => {
    setCartItems(cartItems.filter((_, i) => i !== index));
  };

  const calcularFrete = async () => {
    if (!cep || cartItems.length === 0) {
      toast.error('Preencha o CEP e adicione produtos ao carrinho');
      return;
    }

    setCalculatingFrete(true);
    try {
      const cepDestino = cep.replace(/\D/g, '');

      // Backend deriva CEP de origem por filial; mandamos itens + destino.
      // Enviamos o preço de venda (o que vai na nota) como valor declarado —
      // o frete tem seguro/ad valorem sobre esse valor.
      const itens = cartItems.map(item => ({
        sku: item.sku,
        quantidade: item.quantidade,
        preco: parseFloat(item.preco_unitario) || 0,
      }));

      const response = await axios.post(`${API_URL}/fretes/calcular`, {
        cep_origem: '',  // mantido pra retrocompat — backend ignora
        cep_destino: cepDestino,
        empresa_id: EMPRESA_FRETE_ID,  // mantido pra retrocompat — backend ignora
        itens,
      }, {
        params: {
          cidade_destino: cidade,
          uf_destino: estado,  // retirada em Amparo só para Amparo/SP (há Amparo na PB)
        }
      });

      const data = response.data || {};

      // 1) Produtos sem estoque em filial nenhuma
      const semEstoque = Array.isArray(data.produtos_sem_estoque) ? data.produtos_sem_estoque : [];
      setProdutosSemEstoque(semEstoque);

      // 2) Aviso de leadtime (estoque parcial)
      if (data.aviso_leadtime) {
        const produtos = (data.aviso_leadtime.produtos || []).map(p => p.nome || p.sku).join(', ');
        toast.warning(
          `${data.aviso_leadtime.mensagem}${produtos ? `\nProdutos: ${produtos}` : ''}`,
          { duration: 8000 }
        );
      }

      // 3) Retirada na loja da cidade do cliente (Foz do Iguaçu, Amparo), só quando a loja tem todos os itens
      setRetiradaNaLoja(data.retirada_na_loja || null);
      setRetiradaIndisponivel(data.retirada_indisponivel || null);

      // 4) Segmentos
      const segmentos = Array.isArray(data.segmentos) ? data.segmentos : [];
      setFreteSegmentos(segmentos);

      // 5) Auto-selecionar a opção mais barata por segmento
      const novaSelecao = {};
      let totalSegmentosComOpcoes = 0;
      segmentos.forEach(seg => {
        if (Array.isArray(seg.opcoes) && seg.opcoes.length > 0) {
          const maisBarata = seg.opcoes.reduce((min, op) =>
            (op.valor < min.valor) ? op : min
          , seg.opcoes[0]);
          novaSelecao[seg.empresa_id] = maisBarata;
          totalSegmentosComOpcoes += 1;
        }
      });
      setFreteSelecionadoPorSegmento(novaSelecao);
      // Reseta retirada na loja como opção ativa (usuário pode marcar depois)
      setUsarRetiradaNaLoja(false);

      if (totalSegmentosComOpcoes === 0) {
        toast.warning('Nenhuma opção de frete disponível para esta rota');
      } else if (segmentos.length === 1) {
        toast.success(`Frete calculado: ${segmentos[0].opcoes.length} opções da ${segmentos[0].filial_label}`);
      } else {
        toast.success(
          `Pedido dividido em ${segmentos.length} filiais. Escolha a transportadora de cada uma.`,
          { duration: 6000 }
        );
      }
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (detail && typeof detail === 'object' && detail.produtos_sem_estoque) {
        setProdutosSemEstoque(detail.produtos_sem_estoque);
        toast.error(detail.message || 'Nenhum dos produtos foi encontrado no catálogo de filiais');
      } else {
        const errorMsg = typeof detail === 'string' ? detail : 'Erro ao calcular frete';
        toast.error(errorMsg);
      }
      setFreteSegmentos([]);
      setFreteSelecionadoPorSegmento({});
      // O cálculo falhou: nada do anterior vale (nem a retirada marcada, nem o motivo de indisponível)
      setRetiradaNaLoja(null);
      setUsarRetiradaNaLoja(false);
      setRetiradaIndisponivel(null);
    } finally {
      setCalculatingFrete(false);
    }
  };

  // Helpers multi-segmento
  const selecionarOpcaoSegmento = (empresaId, opcao) => {
    setUsarRetiradaNaLoja(false);
    setFreteSelecionadoPorSegmento(prev => ({ ...prev, [empresaId]: opcao }));
  };

  const todosSegmentosTemSelecao = freteSegmentos.length > 0 && freteSegmentos.every(seg =>
    seg.erro || (seg.opcoes || []).length === 0 || Boolean(freteSelecionadoPorSegmento[seg.empresa_id])
  );

  const valorFreteTotal = (() => {
    if (freteGratis) return 0;
    if (usarRetiradaNaLoja && retiradaNaLoja) return 0;
    return freteSegmentos.reduce((acc, seg) => {
      const sel = freteSelecionadoPorSegmento[seg.empresa_id];
      return acc + (sel?.valor || 0);
    }, 0);
  })();

  // Prazo consolidado = maior prazo entre segmentos (mais conservador)
  const prazoConsolidado = (() => {
    if (freteGratis || (usarRetiradaNaLoja && retiradaNaLoja)) return 0;
    let max = 0;
    freteSegmentos.forEach(seg => {
      const sel = freteSelecionadoPorSegmento[seg.empresa_id];
      const prazo = parseInt(sel?.prazo || sel?.prazo_entrega || 0, 10);
      if (!isNaN(prazo) && prazo > max) max = prazo;
    });
    return max;
  })();

  // Rola até a seção com erro e aplica um destaque temporário (ring).
  const flagSection = (sectionId) => {
    setHighlightSection(sectionId);
    document.getElementById(sectionId)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    setTimeout(() => setHighlightSection(null), HIGHLIGHT_MS);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!clienteCpfCnpj || clienteCpfCnpj.trim() === '') {
      toast.error('O campo CPF/CNPJ é obrigatório');
      flagSection('secao-cliente');
      return;
    }

    if (cartItems.length === 0) {
      toast.error('Adicione produtos ao pedido');
      flagSection('secao-produtos');
      return;
    }

    // Validações de frete: precisa de uma das opções:
    //  - Frete grátis
    //  - Retirada na loja
    //  - Todos os segmentos com opção selecionada
    if (!freteGratis && !(usarRetiradaNaLoja && retiradaNaLoja)) {
      if (freteSegmentos.length === 0) {
        toast.error('Calcule o frete antes de finalizar o pedido');
        flagSection('secao-frete');
        return;
      }
      if (!todosSegmentosTemSelecao) {
        toast.error('Selecione uma opção de frete para cada filial de origem');
        flagSection('secao-frete');
        return;
      }
    }

    if (isProposta && formaPagamento === FINANCIAMENTO && !(parseInt(parcelamento, 10) >= 1 && Number(valorParcela) > 0)) {
      toast.error('Informe o número de parcelas e o valor da parcela do financiamento');
      flagSection('secao-pagamento');
      return;
    }

    setLoading(true);
    let orderData; // fora do try: o 409 "sem_estoque" reenvia o mesmo pedido depois da confirmação
    try {
      // Normalizar cartItems para garantir que todos os números sejam válidos
      const normalizedCartItems = cartItems.map(item => ({
        ...item,
        quantidade: parseInt(item.quantidade) || 1,
        preco_unitario: parseFloat(item.preco_unitario) || 0,
        preco_original: parseFloat(item.preco_original) || 0,
        desconto: parseFloat(item.desconto) || 0,
        // (desconto_percentual doesn't need to be sent back as it's not in db)
        peso: parseFloat(item.peso) || 1,
        altura: parseFloat(item.altura) || 10,
        largura: parseFloat(item.largura) || 10,
        comprimento: parseFloat(item.comprimento) || 10
      }));

      // Consolida o frete escolhido pra um único objeto ShippingOption.
      // Quando o pedido foi fragmentado, anexa segmentos[] com a opção
      // escolhida pra cada filial — o backend usa isso pra criar 1 pedido
      // Tiny por filial mais adiante.
      let freteFinal;
      if (freteGratis) {
        freteFinal = {
          transportadora: 'Frete Grátis',
          servico: '',
          valor: 0,
          prazo_entrega: '',
          gratis: true,
        };
      } else if (usarRetiradaNaLoja && retiradaNaLoja) {
        freteFinal = {
          transportadora: retiradaNaLoja.transportadora,
          servico: retiradaNaLoja.servico || 'Retirada no Local ou Entrega',
          valor: 0,
          prazo_entrega: 0,
          tipo_entrega: 'retirada',
          gratis: false,
          // A loja escolhida: o pedido vai para a conta Tiny dela (sem isso, Filial 5, como os pedidos antigos).
          descricao: retiradaNaLoja.descricao,
          loja_empresa_id: retiradaNaLoja.loja_empresa_id,
          loja_cidade: retiradaNaLoja.loja_cidade,
        };
      } else {
        const segmentosEscolhidos = freteSegmentos
          .map(seg => {
            const sel = freteSelecionadoPorSegmento[seg.empresa_id];
            if (!sel) return null;
            return {
              empresa_id: seg.empresa_id,
              filial_label: seg.filial_label,
              cep_origem: seg.cep_origem,
              transportadora: sel.transportadora,
              servico: sel.servico,
              valor: parseFloat(sel.valor) || 0,
              prazo: parseInt(sel.prazo || sel.prazo_entrega || 0, 10) || 0,
              prazo_entrega: parseInt(sel.prazo || sel.prazo_entrega || 0, 10) || 0,
              tipo_entrega: sel.tipo_entrega || 'normal',
              // Na criação seg.itens são objetos {sku,...}; ao EDITAR, o pedido
              // salvo reidrata com strings. Sem este aceite duplo, a edição
              // gravava [null] e o despacho ao Tiny parava em silêncio.
              itens: (seg.itens || []).map(i => (typeof i === 'string' ? i : i?.sku)).filter(Boolean),
              peso_total_kg: seg.peso_total_kg || 0,
            };
          })
          .filter(Boolean);

        if (segmentosEscolhidos.length > 1) {
          freteFinal = {
            transportadora: 'Multi-filial',
            servico: segmentosEscolhidos.map(s => `${s.filial_label}: ${s.transportadora}`).join(' | '),
            valor: parseFloat(valorFreteTotal) || 0,
            prazo_entrega: prazoConsolidado,
            tipo_entrega: 'normal',
            gratis: false,
            segmentos: segmentosEscolhidos,
          };
        } else if (segmentosEscolhidos.length === 1) {
          const s = segmentosEscolhidos[0];
          freteFinal = {
            transportadora: s.transportadora,
            servico: s.servico,
            valor: s.valor,
            prazo_entrega: s.prazo_entrega,
            tipo_entrega: s.tipo_entrega,
            gratis: false,
            segmentos: segmentosEscolhidos,
          };
        } else {
          freteFinal = {
            transportadora: 'Frete Grátis',
            servico: '',
            valor: 0,
            prazo_entrega: '',
            gratis: true,
          };
        }
      }

      if (isSalesOps && !isEditMode && !vendedorId) {
        toast.error('Selecione o vendedor do pedido');
        return;
      }

      orderData = {
        ...(isSalesOps && !isEditMode && vendedorId ? { vendedor_id: vendedorId } : {}),
        cliente_nome: clienteNome,
        cliente_email: clienteEmail,
        cliente_telefone: clienteTelefone,
        cliente_cpf_cnpj: clienteCpfCnpj,
        endereco_entrega: {
          cep,
          endereco,
          numero,
          complemento,
          bairro,
          cidade,
          estado
        },
        itens: normalizedCartItems,
        frete: freteFinal,
        ...camposDePagamento(formaPagamento, parcelamento, valorParcela),
        observacoes,
        credito_bcmed_valor: creditoDoPedido,
        ...(isProposta ? { validade_dias: validadeDias } : {}),
      };

      const recurso = isProposta ? 'propostas' : 'pedidos';
      if (isEditMode) {
        await axios.put(`${API_URL}/${recurso}/${id}`, orderData);
        toast.success(isProposta ? 'Proposta atualizada com sucesso!' : 'Pedido atualizado com sucesso!');
      } else {
        const res = await axios.post(`${API_URL}/${recurso}`, orderData);
        toast.success(isProposta ? `Proposta ${res.data?.numero_proposta || ''} salva` : 'Pedido criado com sucesso!');
      }

      navigate(voltarPara);
    } catch (error) {
      // Sem estoque livre na filial de saída: o vendedor decide se vende assim mesmo (confirmação abaixo).
      const faltas = faltasDoErro(error);
      if (faltas) {
        setSemEstoque({ faltas, orderData });
        return;
      }
      // O saldo de crédito mudou desde a consulta: o valor volta ao máximo atual e o vendedor salva de novo.
      const recusaCredito = erroDeCredito(error);
      if (recusaCredito) {
        if (recusaCredito.maximo !== undefined) setCreditoValor(String(recusaCredito.maximo));
        toast.error(recusaCredito.mensagem);
        return;
      }

      // Tratamento específico de erros
      if (error.response?.status === 401) {
        toast.error('Sessão expirada. Faça login novamente.');
        navigate('/login');
      } else if (error.response?.status === 403) {
        toast.error('Você não tem permissão para esta ação.');
      } else if (error.response?.status === 422) {
        // Erro de validação do Pydantic
        const detail = error.response?.data?.detail;
        if (Array.isArray(detail)) {
          // Formatar erros de validação
          const errorMessages = detail.map(err => `${err.loc?.join(' → ')}: ${err.msg}`).join('; ');
          toast.error(`Erro de validação: ${errorMessages}`);
        } else if (typeof detail === 'string') {
          toast.error(detail);
        } else {
          toast.error(`Erro de validação ao ${isEditMode ? 'atualizar' : 'criar'} ${isProposta ? 'proposta' : 'pedido'}`);
        }
      } else {
        const detail = error.response?.data?.detail;
        const errorMessage = typeof detail === 'string' ? detail : `Erro ao ${isEditMode ? 'atualizar' : 'criar'} ${isProposta ? 'proposta' : 'pedido'}`;
        toast.error(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  // Venda confirmada sem estoque livre: o mesmo pedido (novo ou editado), que sai marcado como "venda sem estoque".
  const confirmarVendaSemEstoque = async () => {
    setLoading(true);
    const params = { confirmar_sem_estoque: true };
    try {
      if (isEditMode) await axios.put(`${API_URL}/pedidos/${id}`, semEstoque.orderData, { params });
      else await axios.post(`${API_URL}/pedidos`, semEstoque.orderData, { params });
      setSemEstoque(null);
      toast.success(isEditMode ? 'Pedido atualizado com sucesso!' : 'Pedido criado com sucesso!');
      navigate(voltarPara);
    } catch (error) {
      const detail = error.response?.data?.detail;
      setSemEstoque(null);
      toast.error(typeof detail === 'string' ? detail : `Erro ao ${isEditMode ? 'atualizar' : 'criar'} pedido`);
    } finally {
      setLoading(false);
    }
  };

  const subtotal = cartItems.reduce((sum, item) => sum + (item.quantidade * item.preco_unitario), 0);
  const valorFrete = valorFreteTotal;

  const documentoCredito = documentoDoCredito(clienteCpfCnpj);
  const creditoNoFinanciamento = formaPagamento === FINANCIAMENTO;
  const limiteCredito = creditoTravado ? null : limiteDoCredito(saldoCredito, subtotal);
  // Itens e forma entram nas regras do Cash Black (teto por forma, % do preço de tabela); a chave em texto evita
  // consultar de novo a cada render.
  const itensCredito = JSON.stringify(itensDoCredito(cartItems));
  useEffect(() => {
    if (!documentoCredito || creditoNoFinanciamento || creditoTravado) {
      setSaldoCredito(null);
      setCreditoIndisponivel(false);
      return undefined;
    }
    const consulta = ++ultimaConsultaCredito.current;
    const espera = setTimeout(async () => {
      try {
        const res = await axios.post(`${API_URL}/credito-bcmed/saldo`, {
          cpf_cnpj: documentoCredito, subtotal, ...(numeroPedido ? { numero_pedido: numeroPedido } : {}),
          itens: JSON.parse(itensCredito), forma_pagamento: formaPagamento,
        });
        if (consulta !== ultimaConsultaCredito.current) return; // resposta de um documento/subtotal anterior
        setSaldoCredito(res.data);
        setCreditoIndisponivel(false);
      } catch {
        if (consulta !== ultimaConsultaCredito.current) return;
        setSaldoCredito(null);
        setCreditoIndisponivel(true);
      }
    }, 600);
    return () => clearTimeout(espera);
  }, [documentoCredito, subtotal, numeroPedido, creditoNoFinanciamento, creditoTravado, itensCredito, formaPagamento]);
  // Valor acima do máximo (o saldo ou os produtos mudaram) é reduzido, com aviso.
  useEffect(() => {
    const { valor, reduzido } = limitarCredito(creditoValor, limiteCredito);
    if (!reduzido) return;
    setCreditoValor(String(valor));
    toast.warning(`Crédito BCMED reduzido ao máximo deste pedido: ${formatBRL(valor)}`);
  }, [creditoValor, limiteCredito]);
  const creditoDoPedido = creditoTravado ? (Number(creditoValor) || 0) : creditoAplicado({
    valor: creditoValor, documento: documentoCredito, financiamento: creditoNoFinanciamento, limite: limiteCredito,
  });
  const total = subtotal + valorFrete - creditoDoPedido;

  // Derivados para o card de resumo (atualizam em tempo real com o estado existente).
  const totalItens = cartItems.reduce((sum, item) => sum + (parseInt(item.quantidade) || 0), 0);
  const descontoTotal = cartItems.reduce(
    (sum, item) => sum + ((parseFloat(item.desconto) || 0) * (parseInt(item.quantidade) || 0)),
    0
  );

  const formaPagamentoLabel = nomeDaForma(formaPagamento);
  // Financiamento: as parcelas do cliente (na proposta, as digitadas; no pedido, as que a financeira aprovou).
  const parcelasFinanciamento = financiamentoPedido ? financiamentoPedido.parcelas : parcelamento;
  const valorParcelaFinanciamento = financiamentoPedido ? financiamentoPedido.valor_parcela : valorParcela;
  const parcelasResumo = FORMAS_COM_LINK.includes(formaPagamento) ? (parcelamento > 1 ? `até ${parcelamento}x` : 'à vista')
    : formaPagamento === FINANCIAMENTO && parcelasFinanciamento ? `${parcelasFinanciamento}x` : null;
  const pagamentoResumo = parcelasResumo ? `${formaPagamentoLabel} · ${parcelasResumo}` : formaPagamentoLabel;

  const freteResumo = (() => {
    if (freteGratis) return { label: 'Frete grátis', gratis: true };
    if (usarRetiradaNaLoja && retiradaNaLoja) {
      return { label: retiradaNaLoja.loja_cidade ? `Retirada em ${retiradaNaLoja.loja_cidade}` : 'Retirada na loja', gratis: true };
    }
    if (freteSegmentos.length === 0) return { label: 'A calcular', pendente: true };
    const transportadora = freteSegmentos.length > 1
      ? `Multi-filial (${freteSegmentos.length})`
      : (freteSelecionadoPorSegmento[freteSegmentos[0].empresa_id]?.transportadora || 'A selecionar');
    return { label: transportadora, valor: valorFreteTotal };
  })();

  // Linha "12x de R$ ..." sob o total: o cartão divide o total; o financiamento mostra a parcela digitada.
  const linhaParcelas = formaPagamento === FINANCIAMENTO
    ? (parcelasFinanciamento && Number(valorParcelaFinanciamento) > 0
      ? `${parcelasFinanciamento}x de ${formatBRL(valorParcelaFinanciamento)} no financiamento` : null)
    : (FORMAS_COM_LINK.includes(formaPagamento) && parcelamento > 1 && total > 0
      ? `${parcelamento}x de ${formatBRL(total / parcelamento)} no cartão` : null);

  // Card de resumo reutilizado nas duas colunas (sticky no desktop, inline no mobile).
  const renderResumo = () => (
    <Card className="overflow-hidden">
      <SheetHeader title={isProposta ? 'Resumo da proposta' : 'Resumo do pedido'}>
        <span className="text-[12.5px] tabular-nums text-gray-500">
          {totalItens} {totalItens === 1 ? 'item' : 'itens'}
        </span>
      </SheetHeader>
      <CardContent className="px-5 pb-5 pt-4">
        {/* Bloco de valores — lê como recibo, não como lista de propriedades */}
        <dl className="space-y-2 text-sm">
          <div className="flex justify-between gap-3">
            <dt className="text-muted-foreground">Subtotal</dt>
            <dd className="font-medium tabular-nums text-slate-900 dark:text-slate-100">{formatBRL(subtotal)}</dd>
          </div>
          {descontoTotal > 0 && (
            <div className="flex justify-between gap-3">
              <dt className="text-muted-foreground">Desconto</dt>
              <dd className="font-medium tabular-nums text-slate-500 dark:text-slate-400">− {formatBRL(descontoTotal)}</dd>
            </div>
          )}
          <div className="flex justify-between gap-3">
            <dt className="text-muted-foreground">Frete</dt>
            <dd className="text-right font-medium tabular-nums text-slate-900 dark:text-slate-100">
              {freteResumo.pendente ? (
                <span className="font-normal text-muted-foreground/70">A calcular</span>
              ) : freteResumo.gratis ? (
                <span className="text-emerald-600 dark:text-emerald-400">{freteResumo.label}</span>
              ) : (
                <>
                  <span className="block text-xs font-normal text-muted-foreground truncate max-w-[9rem] ml-auto">
                    {freteResumo.label}
                  </span>
                  {formatBRL(freteResumo.valor)}
                </>
              )}
            </dd>
          </div>
          {creditoDoPedido > 0 && (
            <div className="flex justify-between gap-3">
              <dt className="text-muted-foreground">Crédito BCMED</dt>
              <dd className="font-medium tabular-nums text-slate-500 dark:text-slate-400">− {formatBRL(creditoDoPedido)}</dd>
            </div>
          )}
          <div className="flex justify-between gap-3">
            <dt className="text-muted-foreground">Pagamento</dt>
            <dd className="text-right font-medium text-slate-900 dark:text-slate-100">{pagamentoResumo}</dd>
          </div>
        </dl>

        {!creditoNoFinanciamento && (
          <CreditoBcmed
            saldo={saldoCredito}
            indisponivel={creditoIndisponivel}
            valor={creditoValor}
            onValor={setCreditoValor}
            limite={limiteCredito}
            travado={creditoTravado && Number(creditoValor) > 0}
          />
        )}

        <div className="mt-4 border-t border-border pt-4">
          <div className="flex items-baseline justify-between">
            <span className="text-sm font-semibold text-slate-900 dark:text-slate-100">Total</span>
            <span className="text-[28px] font-medium leading-none tabular-nums tracking-[-0.02em]">
              {formatBRL(total)}
            </span>
          </div>
          {linhaParcelas && (
            <p className="mt-1 text-right text-xs tabular-nums text-muted-foreground">{linhaParcelas}</p>
          )}
          {totalItens === 0 && (
            <p className="mt-1 text-right text-xs text-muted-foreground">
              Adicione produtos para compor o pedido
            </p>
          )}
        </div>

        <div className="mt-5 flex gap-2.5">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate(voltarPara)}
            className="flex-1"
            data-testid="cancel-order-button"
          >
            Cancelar
          </Button>
          <Button
            type="submit"
            className="flex-[1.4]"
            disabled={loading}
            data-testid="submit-order-button"
          >
            {loading
              ? (isProposta ? 'Salvando' : (isEditMode ? 'Atualizando' : 'Criando'))
              : (isProposta ? 'Salvar proposta' : (isEditMode ? 'Atualizar' : 'Criar pedido'))}
          </Button>
        </div>
      </CardContent>
    </Card>
  );

  if (loadingOrder) {
    return (
      <div>
        <PageCover title={isProposta ? 'Proposta' : 'Pedido'} subtitle="Carregando" />
        <PageBody>
          <Card>
            <div aria-hidden className="space-y-3 px-5 py-6">
              <div className="h-4 w-56 animate-pulse rounded bg-muted" />
              <div className="h-4 w-80 animate-pulse rounded bg-muted" />
              <div className="h-4 w-64 animate-pulse rounded bg-muted" />
            </div>
          </Card>
        </PageBody>
      </div>
    );
  }

  return (
    <div data-testid="create-order-page">
      <PageCover
        title={isProposta ? (isEditMode ? 'Editar proposta' : 'Nova proposta') : (isEditMode ? 'Editar pedido' : 'Novo pedido')}
        subtitle={isProposta
          ? (isEditMode ? 'Ajuste as condições da proposta'
            : formaPagamento === FINANCIAMENTO ? 'Monte a proposta comercial. Ela vira pedido quando a financeira aprovar.'
              : 'Monte a proposta comercial. Ela vira pedido quando o cliente aceitar.')
          : (isEditMode ? 'Ajuste as informações do pedido' : 'Pedido de venda, do cliente ao pagamento')}
      />
      <PageBody>
      <form onSubmit={handleSubmit}>
        <div className="flex flex-col gap-5 lg:flex-row lg:items-start">
          {/* Coluna do formulário */}
          <div className="flex-1 min-w-0 space-y-5">
        {/* Sales Ops: escolhe em nome de qual vendedor o pedido nasce */}
        {isSalesOps && !isEditMode && (
          <div className="space-y-2 rounded-sheet border border-transparent bg-card p-5 shadow-sheet dark:border-border dark:shadow-none" id="secao-vendedor">
            <Label htmlFor="vendedor-pedido">Vendedor do pedido *</Label>
            <Select value={vendedorId} onValueChange={setVendedorId}>
              <SelectTrigger id="vendedor-pedido" data-testid="select-vendedor-pedido">
                <SelectValue placeholder="Selecione o vendedor" />
              </SelectTrigger>
              <SelectContent>
                {vendedores.map((v) => (
                  <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-slate-500 dark:text-slate-400">
              O pedido nasce em nome do vendedor escolhido: o link de pagamento e o pedido no Tiny saem no nome dele.
            </p>
          </div>
        )}

        {/* Customer Info */}
        <Accordion type="multiple" defaultValue={["cliente", "endereco", "produtos", "frete", "pagamento"]} className="w-full space-y-5">

          <AccordionItem value="cliente" id="secao-cliente" className={`rounded-sheet border border-transparent bg-card shadow-sheet dark:border-border dark:shadow-none overflow-hidden transition-shadow duration-300 ${highlightSection === 'secao-cliente' ? 'ring-2 ring-rose-500/60' : ''}`}>
            <AccordionTrigger className="px-5 py-4 hover:no-underline transition-colors hover:bg-muted/50">
              <div className="flex items-center gap-3">
                <StepBadge n={1} />
                <h3 className="text-[15px] font-semibold">Dados do cliente</h3>
              </div>
            </AccordionTrigger>
            <AccordionContent className="px-5 pb-5 pt-0 border-t border-border">
              <div className="space-y-4 pt-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="cliente-nome">Nome completo *</Label>
                    <Input
                      id="cliente-nome"
                      value={clienteNome}
                      onChange={(e) => setClienteNome(e.target.value)}
                      className={FIELD_CLS}
                      required
                      data-testid="input-cliente-nome"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="cliente-email">Email *</Label>
                    <Input
                      id="cliente-email"
                      type="email"
                      value={clienteEmail}
                      onChange={(e) => setClienteEmail(e.target.value)}
                      className={FIELD_CLS}
                      required
                      data-testid="input-cliente-email"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="cliente-telefone">Telefone *</Label>
                    <Input
                      id="cliente-telefone"
                      value={clienteTelefone}
                      onChange={(e) => setClienteTelefone(e.target.value)}
                      className={FIELD_CLS}
                      required
                      data-testid="input-cliente-telefone"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="cliente-cpf">CPF/CNPJ *</Label>
                    <Input
                      id="cliente-cpf"
                      value={clienteCpfCnpj}
                      onChange={(e) => setClienteCpfCnpj(e.target.value)}
                      className={FIELD_CLS}
                      required
                      data-testid="input-cliente-cpf"
                    />
                  </div>
                </div>
              </div>
            </AccordionContent>
          </AccordionItem>

          {/* Shipping Address */}
          <AccordionItem value="endereco" className="rounded-sheet border border-transparent bg-card shadow-sheet dark:border-border dark:shadow-none overflow-hidden">
            <AccordionTrigger className="px-5 py-4 hover:no-underline transition-colors hover:bg-muted/50">
              <div className="flex items-center gap-3">
                <StepBadge n={2} />
                <h3 className="text-[15px] font-semibold">Endereço de entrega</h3>
              </div>
            </AccordionTrigger>
            <AccordionContent className="px-5 pb-5 pt-0 border-t border-border">
              <div className="space-y-4 pt-4">
                {enderecoTravado && (
                  <p role="note" className="flex items-start gap-2 rounded-lg bg-sheet2 px-3 py-2 text-sm text-gray-700 dark:text-gray-300">
                    <Lock className="h-4 w-4 mt-0.5 flex-shrink-0" />
                    Endereço bloqueado: o checkout de pagamento já foi gerado. Para alterar, fale com o administrativo.
                  </p>
                )}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="cep">CEP *</Label>
                    <div className="relative">
                      <Input
                        id="cep"
                        value={cep}
                        onChange={handleCepChange}
                        placeholder="00000-000"
                        maxLength={9}
                        required
                        data-testid="input-cep"
                        readOnly={enderecoTravado}
                        className={`${FIELD_CLS} ${searchingCep ? 'pr-10' : ''} ${enderecoTravado ? TRAVADO_CLS : ''}`}
                      />
                      {searchingCep && (
                        <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                          <Loader2 className="h-4 w-4 animate-spin text-blue-600" />
                        </div>
                      )}
                    </div>
                    {!enderecoTravado && (
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        O endereço será preenchido automaticamente
                      </p>
                    )}
                  </div>
                  <div className="space-y-2 md:col-span-2">
                    <Label htmlFor="endereco">Endereço *</Label>
                    <Input
                      id="endereco"
                      value={endereco}
                      onChange={(e) => setEndereco(e.target.value)}
                      readOnly={enderecoTravado}
                      className={`${FIELD_CLS} ${enderecoTravado ? TRAVADO_CLS : ''}`}
                      required
                      data-testid="input-endereco"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="numero">Número *</Label>
                    <div className="relative">
                      <Input
                        id="numero"
                        value={numero}
                        onChange={(e) => setNumero(e.target.value)}
                        required
                        disabled={numero.toUpperCase() === 'S/N'}
                        readOnly={enderecoTravado}
                        className={`${FIELD_CLS} pr-12 ${enderecoTravado ? TRAVADO_CLS : ''}`}
                        data-testid="input-numero"
                      />
                      <button
                        type="button"
                        disabled={enderecoTravado}
                        onClick={() => {
                          if (numero.toUpperCase() === 'S/N') setNumero('');
                          else setNumero('S/N');
                        }}
                        className={`absolute right-1.5 top-1/2 -translate-y-1/2 text-[11px] font-semibold px-2.5 py-1 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${numero.toUpperCase() === 'S/N'
                          ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-400'
                          : 'bg-slate-200 text-slate-600 hover:bg-slate-300 dark:bg-slate-800/80 dark:text-slate-400 dark:hover:bg-slate-700'
                          }`}
                        title="Marcar como Sem Número"
                      >
                        S/N
                      </button>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="complemento">Complemento</Label>
                    <Input
                      id="complemento"
                      value={complemento}
                      onChange={(e) => setComplemento(e.target.value)}
                      readOnly={enderecoTravado}
                      className={`${FIELD_CLS} ${enderecoTravado ? TRAVADO_CLS : ''}`}
                      data-testid="input-complemento"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="bairro">Bairro *</Label>
                    <Input
                      id="bairro"
                      value={bairro}
                      onChange={(e) => setBairro(e.target.value)}
                      readOnly={enderecoTravado}
                      className={`${FIELD_CLS} ${enderecoTravado ? TRAVADO_CLS : ''}`}
                      required
                      data-testid="input-bairro"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="cidade">Cidade *</Label>
                    <Input
                      id="cidade"
                      value={cidade}
                      onChange={(e) => setCidade(e.target.value)}
                      readOnly={enderecoTravado}
                      className={`${FIELD_CLS} ${enderecoTravado ? TRAVADO_CLS : ''}`}
                      required
                      data-testid="input-cidade"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="estado">Estado *</Label>
                    <Input
                      id="estado"
                      value={estado}
                      onChange={(e) => setEstado(e.target.value)}
                      maxLength={2}
                      readOnly={enderecoTravado}
                      className={`${FIELD_CLS} ${enderecoTravado ? TRAVADO_CLS : ''}`}
                      required
                      data-testid="input-estado"
                    />
                  </div>
                </div>
              </div>
            </AccordionContent>
          </AccordionItem>

          {/* Products */}
          <AccordionItem value="produtos" id="secao-produtos" className={`rounded-sheet border border-transparent bg-card shadow-sheet dark:border-border dark:shadow-none overflow-hidden transition-shadow duration-300 ${highlightSection === 'secao-produtos' ? 'ring-2 ring-rose-500/60' : ''}`}>
            <AccordionTrigger className="px-5 py-4 hover:no-underline transition-colors hover:bg-muted/50">
              <div className="flex items-center gap-3">
                <StepBadge n={3} />
                <div className="text-left">
                  <h3 className="text-[15px] font-semibold">Produtos</h3>
                  <p className="text-xs text-muted-foreground font-normal mt-0.5">O frete será calculado a partir da Filial 5</p>
                </div>
              </div>
            </AccordionTrigger>
            <AccordionContent className="px-5 pb-5 pt-0 border-t border-border">
              <div className="space-y-4 pt-4">
                {/* Busca de produtos */}
                <div className="space-y-2">
                  <Label>Adicionar produtos</Label>
                  <div className="relative">
                    <div className="relative group">
                      <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-slate-400 group-focus-within:text-blue-500 transition-colors" />
                      <Input
                        value={productSearch}
                        onChange={(e) => setProductSearch(e.target.value)}
                        placeholder="Digite o SKU ou nome do produto para buscar..."
                        className={`${FIELD_CLS} pl-12 pr-10 h-12 text-base`}
                        data-testid="input-product-search"
                      />
                      {productSearch && (
                        <button
                          type="button"
                          onClick={() => {
                            setProductSearch('');
                            setShowProductDropdown(false);
                          }}
                          className="absolute right-4 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200"
                        >
                          <X className="h-5 w-5" />
                        </button>
                      )}
                    </div>

                    {/* Dropdown de resultados (Renderizado estático para não ser cortado pelo Accordion) */}
                    {showProductDropdown && filteredProducts.length > 0 && (
                      <div className="relative z-10 mt-2 w-full overflow-hidden rounded-lg border border-border bg-popover shadow-pop">
                        <div className="max-h-[300px] overflow-y-auto">
                          {filteredProducts.map((product) => (
                            <button
                              key={product.sku}
                              type="button"
                              onClick={() => addToCart(product.sku)}
                              className="group w-full border-b border-border px-5 py-3 text-left transition-colors last:border-b-0 hover:bg-muted/50"
                              data-testid={`search-result-${product.sku}`}
                            >
                              <div className="flex items-center justify-between">
                                <div className="flex-1 pr-4">
                                  <p className="font-semibold text-slate-900 dark:text-slate-100 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors truncate">{product.nome}</p>
                                  <p className="mt-0.5 text-[12.5px] text-gray-500">SKU {product.sku}</p>
                                </div>
                                <span className="text-sm font-bold text-slate-900 dark:text-white bg-slate-100 dark:bg-slate-800 px-3 py-1 rounded-md">
                                  {formatBRL(product.preco)}
                                </span>
                              </div>
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Mensagem quando não há resultados */}
                {productSearch.trim().length >= 2 && !showProductDropdown && (
                  <div className="text-sm text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-950/30 font-medium p-3 rounded-lg border border-amber-200 dark:border-amber-900/50 mt-2">
                    Nenhum produto encontrado para "{productSearch}"
                  </div>
                )}

                {/* Stock by branch */}
                {loadingStock && (
                  <div className="flex items-center justify-center py-4">
                    <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
                    <span className="ml-2 text-sm text-gray-600 dark:text-gray-400">
                      Consultando estoque nas filiais...
                    </span>
                  </div>
                )}

                {/* Exibir aviso de leadtime quando não houver estoque */}
                {productLeadtime && (
                  <div className="rounded-lg bg-sheet2 px-4 py-3">
                    <StatusDot tone="att">Produto sem estoque disponível</StatusDot>
                    <p className="mt-1 pl-[15px] text-[13px] text-gray-600 dark:text-gray-300">
                      Prazo do fornecedor: <span className="font-medium text-foreground">{productLeadtime.prazo_fornecedor}</span>. O pedido pode seguir normalmente.
                    </p>
                  </div>
                )}

                {selectedProductStock && selectedProductStock.length > 0 && (
                  <div className="rounded-lg border border-border">
                    <div className="flex items-center justify-between gap-3 border-b border-border px-4 py-2.5">
                      <h4 className="text-[13px] font-medium">Estoque do último item buscado</h4>
                      <StatusDot tone={selectedProductStock[0]?.saida ? 'ok' : 'off'} className="text-[12.5px]">
                        {selectedProductStock[0]?.saida ? 'Saída conferida no Tiny' : 'Base local'}
                      </StatusDot>
                    </div>
                    <ul className="grid grid-cols-2 gap-2 p-3 sm:grid-cols-3 xl:grid-cols-5">
                      {selectedProductStock.map((stock) => {
                        // Com a filial de saída em tempo real, só ela diz se dá para vender; as outras são informação.
                        const informativa = selectedProductStock[0]?.saida && !stock.saida;
                        const tom = informativa ? 'off'
                          : stock.saldo_disponivel > 10 ? 'ok' : stock.saldo_disponivel > 0 ? 'att' : 'bad';
                        return (
                          <li key={stock.empresa_nome} className="min-w-0 rounded-md bg-sheet2 px-3 py-2.5">
                            <p className="truncate text-[12.5px] text-gray-500">{stock.empresa_nome}{stock.saida ? ' (saída)' : ''}</p>
                            <p className="mt-1 flex items-center gap-2">
                              <span aria-hidden className={`h-[7px] w-[7px] rounded-full ${DOT[tom]}`} />
                              <span className="text-[20px] font-medium leading-none tabular-nums">{stock.saldo_disponivel}</span>
                            </p>
                            <p className="mt-1 text-[12px] tabular-nums text-gray-500">
                              {informativa
                                ? (stock.saldo > 0 ? 'Precisa transferir' : 'Sem estoque')
                                : `${stock.saida ? 'Livre. ' : ''}Total ${stock.saldo}${stock.saldo_reservado > 0 ? `, ${stock.saldo_reservado} reservado` : ''}`}
                            </p>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                )}

                {selectedProductStock && selectedProductStock.length === 0 && !loadingStock && (
                  <div className="rounded-lg bg-sheet2 px-4 py-3">
                    <StatusDot tone="att">Nenhum estoque encontrado nas filiais. Verifique se o produto existe no Tiny.</StatusDot>
                  </div>
                )}

                {cartItems.length > 0 && (
                  <div className="space-y-3 mt-4">
                    {cartItems.map((item, index) => (
                      <div key={`${item.sku}-${index}`} className="group relative flex flex-col xl:flex-row xl:items-center justify-between gap-4 rounded-lg border border-border bg-card p-4" data-testid={`cart-item-${index}`}>

                        <div className="flex-1 min-w-0 pr-4">
                          <p className="font-semibold text-slate-900 dark:text-white truncate" title={item.nome}>{item.nome}</p>
                          <div className="flex items-center gap-3 mt-1.5">
                            <span className="text-[12.5px] text-gray-500">SKU {item.sku}</span>
                            {item.desconto > 0 && item.preco_original && item.preco_unitario < item.preco_original && (
                              <span className="text-xs text-blue-600 dark:text-blue-400 font-medium">
                                -{((item.desconto / item.preco_original) * 100).toFixed(0)}% (Economia de {formatBRL(item.desconto)})
                              </span>
                            )}
                          </div>
                        </div>

                        <div className="flex flex-wrap items-end xl:items-center gap-3 xl:gap-5 bg-sheet2 p-3 xl:p-0 xl:bg-transparent rounded-lg ml-auto">

                          {/* Unit Price */}
                          <div className="flex flex-col gap-1.5 w-full sm:w-auto">
                            <Label className="text-[12px] font-medium text-gray-500">Valor Un.</Label>
                            <div className="relative">
                              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm font-medium">R$</span>
                              <Input
                                type="number"
                                step="0.01"
                                min="0" // Note: the user might want a limit on discount
                                value={item.preco_unitario}
                                onChange={(e) => updatePreco(index, e.target.value)}
                                className={`${FIELD_CLS} w-full sm:w-28 pl-9 h-9 text-sm font-bold [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none`}
                                data-testid={`price-input-${index}`}
                              />
                            </div>
                          </div>

                          {/* Discount % */}
                          <div className="flex flex-col gap-1.5 w-full sm:w-auto">
                            <Label className="text-[12px] font-medium text-gray-500">Desc. (%)</Label>
                            <div className="relative">
                              <Input
                                type="number"
                                step="0.01"
                                min="0"
                                max="100"
                                value={item.desconto_percentual !== undefined ? item.desconto_percentual : ''}
                                onChange={(e) => updateDescontoPercentual(index, e.target.value)}
                                className="w-full sm:w-20 pr-7 pl-2 h-9 text-sm font-bold text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800 focus-visible:ring-blue-500 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                data-testid={`discount-pct-input-${index}`}
                              />
                              <span className="absolute right-2 top-1/2 -translate-y-1/2 text-blue-500 text-sm font-medium">%</span>
                            </div>
                          </div>

                          {/* Qty */}
                          <div className="flex flex-col gap-1.5">
                            <Label className="text-[12px] font-medium text-gray-500">Qtd.</Label>
                            <div className="flex items-center bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg h-9 overflow-hidden">
                              <button type="button" onClick={() => updateQuantity(index, item.quantidade - 1)} className="px-3 h-full text-slate-500 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/30 transition-colors border-r border-slate-200 dark:border-slate-800" data-testid={`decrease-qty-${index}`}>
                                <Minus className="h-3.5 w-3.5" />
                              </button>
                              <input
                                type="number"
                                value={item.quantidade}
                                onChange={(e) => updateQuantity(index, parseInt(e.target.value) || 1)}
                                className="w-12 h-full text-center text-sm font-bold bg-transparent border-0 ring-0 focus:ring-0 appearance-none m-0 p-0 [&::-webkit-inner-spin-button]:appearance-none"
                                data-testid={`qty-input-${index}`}
                              />
                              <button type="button" onClick={() => updateQuantity(index, item.quantidade + 1)} className="px-3 h-full text-slate-500 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/30 transition-colors border-l border-slate-200 dark:border-slate-800" data-testid={`increase-qty-${index}`}>
                                <Plus className="h-3.5 w-3.5" />
                              </button>
                            </div>
                          </div>

                          {/* Divider */}
                          <div className="hidden xl:block w-px h-8 bg-slate-200 dark:bg-slate-800 mx-2"></div>

                          {/* Total */}
                          <div className="flex flex-col gap-1.5 min-w-[110px] text-right">
                            <Label className="text-[12px] font-medium text-gray-500 text-right">Subtotal</Label>
                            <div className="flex flex-col items-end">
                              <p className="font-semibold text-blue-600 dark:text-blue-400 text-lg leading-none">
                                {formatBRL(item.quantidade * item.preco_unitario)}
                              </p>
                              {item.preco_original && item.preco_unitario !== item.preco_original && (
                                <span className="text-[12px] text-gray-500 line-through mt-0.5">
                                  {formatBRL(item.quantidade * item.preco_original)}
                                </span>
                              )}
                            </div>
                          </div>

                          {/* Remove */}
                          <Button
                            type="button"
                            size="icon"
                            variant="ghost"
                            onClick={() => removeFromCart(index)}
                            className="h-9 w-9 xl:ml-2 text-slate-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-950/30 rounded-lg transition-colors"
                            data-testid={`remove-item-${index}`}
                            title="Remover"
                          >
                            <Trash2 className="h-4.5 w-4.5" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </AccordionContent>
          </AccordionItem>

          {/* Shipping */}
          <AccordionItem value="frete" id="secao-frete" className={`rounded-sheet border border-transparent bg-card shadow-sheet dark:border-border dark:shadow-none overflow-hidden transition-shadow duration-300 ${highlightSection === 'secao-frete' ? 'ring-2 ring-rose-500/60' : ''}`}>
            <AccordionTrigger className="px-5 py-4 hover:no-underline transition-colors hover:bg-muted/50">
              <div className="flex items-center gap-3">
                <StepBadge n={4} />
                <h3 className="text-[15px] font-semibold">Frete e entrega</h3>
              </div>
            </AccordionTrigger>
            <AccordionContent className="px-5 pb-5 pt-0 border-t border-border">
              <div className="pt-4 mb-4 flex items-center justify-end">
                {!freteGratis && (
                  <Button
                    type="button"
                    onClick={calcularFrete}
                    disabled={calculatingFrete || !cep || cartItems.length === 0}
                    data-testid="calcular-frete-button"
                  >
                    <Calculator className="mr-2 h-4 w-4" />
                    {calculatingFrete ? 'Calculando' : 'Calcular frete'}
                  </Button>
                )}
              </div>
              <div className="space-y-4">
                {/* Checkbox Frete Grátis */}
                <div className="mb-4 flex items-center space-x-2 rounded-lg border border-border p-3">
                  <Checkbox
                    id="frete-gratis"
                    checked={freteGratis}
                    onCheckedChange={setFreteGratis}
                    data-testid="checkbox-frete-gratis"
                  />
                  <label
                    htmlFor="frete-gratis"
                    className="text-sm font-medium text-gray-900 dark:text-gray-100 cursor-pointer"
                  >
                    Frete grátis (zera o valor do frete)
                  </label>
                </div>
                {freteGratis ? (
                  <div className="rounded-lg bg-sheet2 px-4 py-3">
                    <StatusDot tone="ok">Frete grátis aplicado</StatusDot>
                    <p className="mt-1 pl-[15px] text-[13px] text-gray-600 dark:text-gray-300">O valor do frete fica zerado neste pedido.</p>
                  </div>
                ) : (
                  <>
                    {/* Aviso de produtos sem estoque — informativo, não bloqueia a venda */}
                    {produtosSemEstoque.length > 0 && (
                      <div className="mb-3 rounded-lg bg-sheet2 px-4 py-3">
                        <StatusDot tone="att">Sem estoque livre na filial de saída. A venda segue como encomenda, com prazo maior.</StatusDot>
                        <ul className="mt-2 space-y-0.5 pl-[15px] text-[13px] text-gray-600 dark:text-gray-300">
                          {produtosSemEstoque.map((p) => (
                            <li key={p.sku}>{p.nome} <span className="font-mono text-gray-500">({p.sku})</span></li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* Aviso multi-segmento */}
                    {freteSegmentos.length > 1 && (
                      <div className="mb-3 rounded-lg bg-sheet2 px-4 py-3">
                        <StatusDot tone="run">Pedido dividido em {freteSegmentos.length} filiais</StatusDot>
                        <p className="mt-1 pl-[15px] text-[13px] text-gray-600 dark:text-gray-300">
                          O cliente recebe um envio por filial. Escolha a transportadora de cada uma.
                        </p>
                      </div>
                    )}

                    {/* Opção de retirada na loja */}
                    {retiradaNaLoja && (
                      <div
                        onClick={() => {
                          if (usarRetiradaNaLoja) {
                            setUsarRetiradaNaLoja(false);
                          } else {
                            setUsarRetiradaNaLoja(true);
                          }
                        }}
                        className={`p-4 border rounded-lg cursor-pointer transition-all mb-3 ${usarRetiradaNaLoja
                          ? 'border-emerald-500 bg-emerald-50/50 dark:bg-emerald-900/20 ring-1 ring-emerald-500'
                          : 'border-slate-200 dark:border-slate-800 bg-card hover:border-emerald-300'
                          }`}
                        data-testid="frete-retirada-na-loja"
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-4">
                            <div className={`w-4 h-4 rounded-full border flex items-center justify-center ${usarRetiradaNaLoja ? 'border-emerald-500' : 'border-slate-300 dark:border-slate-600'}`}>
                              {usarRetiradaNaLoja && <div className="w-2 h-2 bg-emerald-500 rounded-full" />}
                            </div>
                            <div>
                              <p className="font-semibold">{retiradaNaLoja.transportadora}</p>
                              <p className="text-xs text-slate-500 mt-0.5">{retiradaNaLoja.descricao || retiradaNaLoja.servico}</p>
                            </div>
                          </div>
                          <p className="text-lg font-semibold text-emerald-600 dark:text-emerald-400">Grátis</p>
                        </div>
                      </div>
                    )}

                    {/* Cidade com loja, mas a loja não tem todos os itens: sem retirada, com o motivo */}
                    {!retiradaNaLoja && retiradaIndisponivel && (
                      <div className="mb-3 rounded-lg bg-sheet2 px-4 py-3" data-testid="retirada-indisponivel">
                        <StatusDot tone="off" className="text-[13px]">{retiradaIndisponivel}</StatusDot>
                      </div>
                    )}

                    {/* Segmentos (1 card por filial origem) */}
                    {freteSegmentos.length > 0 ? (
                      <div className="space-y-4">
                        {freteSegmentos.map((seg) => (
                          <div
                            key={seg.empresa_id}
                            className={`border rounded-lg overflow-hidden ${usarRetiradaNaLoja
                              ? 'border-slate-200 dark:border-slate-800 opacity-50'
                              : 'border-slate-200 dark:border-slate-800'
                              }`}
                            data-testid={`frete-segmento-${seg.empresa_id}`}
                          >
                            <div className="flex items-center justify-between border-b border-border bg-sheet2 px-4 py-3">
                              <div>
                                <p className="text-sm font-semibold">Saindo de {seg.filial_label}</p>
                                <p className="mt-0.5 text-[12.5px] text-gray-500">
                                  CEP de origem {seg.cep_origem || '—'} · {(seg.peso_total_kg || 0).toFixed(2)} kg · {(seg.itens || []).length} item(ns)
                                </p>
                              </div>
                              {seg.itens && seg.itens.length > 0 && (
                                <details className="relative text-xs text-slate-500">
                                  <summary className="cursor-pointer select-none font-medium text-slate-500 transition-colors hover:text-blue-600 dark:hover:text-blue-400">
                                    Ver SKUs / dimensões
                                  </summary>
                                  <div className="absolute right-0 top-full z-20 mt-2 max-h-64 w-72 overflow-auto rounded-lg border border-border bg-popover p-3 shadow-pop">
                                    {seg.itens.map((it, idx) => (
                                      <div
                                        key={idx}
                                        className="py-2 first:pt-0 last:pb-0 border-b border-slate-100 dark:border-slate-800 last:border-0"
                                      >
                                        <div className="flex items-center justify-between gap-2">
                                          <span className="font-mono text-slate-700 dark:text-slate-200">{it.sku}</span>
                                          <span className="tabular-nums text-gray-500">× {it.quantidade}</span>
                                        </div>
                                        {it.nome && (
                                          <div className="mt-0.5 truncate text-[11px] text-slate-500">
                                            {it.nome}
                                          </div>
                                        )}
                                        <div className="mt-0.5 text-[11px] tabular-nums text-slate-500">
                                          {Number(it.peso || 0).toFixed(2)} kg · {Number(it.altura || 0)}×{Number(it.largura || 0)}×{Number(it.comprimento || 0)} cm
                                          <span className="opacity-60"> (A×L×C)</span>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </details>
                              )}
                            </div>

                            <div className="p-3 space-y-2">
                              {seg.erro ? (
                                <div className="px-2 py-2"><StatusDot tone="bad">{seg.erro}</StatusDot></div>
                              ) : (seg.opcoes || []).length === 0 ? (
                                <p className="text-xs text-slate-500 italic px-2 py-2">
                                  Nenhuma transportadora disponível para esta filial. Verifique configuração no Tiny.
                                </p>
                              ) : (
                                seg.opcoes.map((opcao, idx) => {
                                  const selecionada = freteSelecionadoPorSegmento[seg.empresa_id];
                                  const isSelected = !usarRetiradaNaLoja && mesmaOpcaoDeFrete(selecionada, opcao);
                                  return (
                                    <div
                                      key={`${seg.empresa_id}-${idx}`}
                                      onClick={() => selecionarOpcaoSegmento(seg.empresa_id, opcao)}
                                      className={`p-3 border rounded-lg cursor-pointer transition-all ${isSelected
                                        ? 'border-blue-500 bg-blue-50/50 dark:bg-blue-900/20 ring-1 ring-blue-500'
                                        : 'border-slate-200 dark:border-slate-800 bg-card hover:border-blue-300'
                                        }`}
                                      data-testid={`frete-option-${seg.empresa_id}-${idx}`}
                                    >
                                      <div className="flex items-center justify-between gap-3">
                                        <div className="flex items-center gap-3 min-w-0">
                                          <div className={`w-4 h-4 flex-shrink-0 rounded-full border flex items-center justify-center ${isSelected ? 'border-blue-500' : 'border-slate-300 dark:border-slate-600'}`}>
                                            {isSelected && <div className="w-2 h-2 bg-blue-500 rounded-full" />}
                                          </div>
                                          <div className="min-w-0">
                                            <p className="font-bold text-sm text-slate-900 dark:text-white truncate">
                                              {opcao.transportadora}
                                            </p>
                                            <div className="flex flex-wrap items-center gap-2 mt-1">
                                              <span className="text-[12px] text-gray-500">
                                                {opcao.prazo > 0 ? `${opcao.prazo} dias úteis` : 'Imediato'}
                                              </span>
                                              {opcao.servico && opcao.servico !== opcao.transportadora && (
                                                <span className="truncate text-[12px] text-gray-500">{opcao.servico}</span>
                                              )}
                                            </div>
                                          </div>
                                        </div>
                                        <p className="shrink-0 text-base font-semibold tabular-nums">
                                          {opcao.valor === 0 ? (
                                            <span className="text-emerald-600 dark:text-emerald-400">Grátis</span>
                                          ) : (
                                            formatBRL(opcao.valor)
                                          )}
                                        </p>
                                      </div>
                                    </div>
                                  );
                                })
                              )}
                            </div>
                          </div>
                        ))}

                        {/* Resumo multi-segmento */}
                        {freteSegmentos.length > 1 && !usarRetiradaNaLoja && (
                          <div className="flex items-center justify-between rounded-lg bg-sheet2 p-3">
                            <span className="text-sm font-medium">Frete total das {freteSegmentos.length} filiais</span>
                            <span className="text-lg font-semibold tabular-nums">{formatBRL(valorFreteTotal)}</span>
                          </div>
                        )}
                      </div>
                    ) : (
                      <p className="py-4 text-sm text-gray-500">Calcule o frete para ver as opções.</p>
                    )}
                  </>
                )}
              </div>
            </AccordionContent>
          </AccordionItem>

          {/* Payment */}
          <AccordionItem value="pagamento" id="secao-pagamento" className={`rounded-sheet border border-transparent bg-card shadow-sheet dark:border-border dark:shadow-none overflow-hidden transition-shadow duration-300 ${highlightSection === 'secao-pagamento' ? 'ring-2 ring-rose-500/60' : ''}`}>
            <AccordionTrigger className="px-5 py-4 hover:no-underline transition-colors hover:bg-muted/50">
              <div className="flex items-center gap-3">
                <StepBadge n={5} />
                <h3 className="text-[15px] font-semibold">Forma de pagamento</h3>
              </div>
            </AccordionTrigger>
            <AccordionContent className="px-5 pb-5 pt-0 border-t border-border">
              <div className="space-y-4 pt-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="forma-pagamento">Forma de pagamento *</Label>
                    {financiamentoPedido ? (
                      <Input
                        id="forma-pagamento" value={resumoFinanciamento({ financiamento: financiamentoPedido })} readOnly disabled
                        className={`${FIELD_CLS} ${TRAVADO_CLS}`} data-testid="forma-pagamento-financiamento"
                      />
                    ) : (
                      <Select value={formaPagamento} onValueChange={trocarFormaPagamento}>
                        <SelectTrigger id="forma-pagamento" className={FIELD_CLS} data-testid="select-forma-pagamento">
                          <SelectValue placeholder="Selecione a forma de pagamento" />
                        </SelectTrigger>
                        <SelectContent>
                          {FORMAS_DO_FORMULARIO.filter((f) => isProposta || f !== FINANCIAMENTO).map((f) => (
                            <SelectItem key={f} value={f}>{nomeDaForma(f)}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  </div>

                  {FORMAS_COM_LINK.includes(formaPagamento) && (
                    <div className="space-y-2">
                      <Label htmlFor="parcelamento">Parcelamento</Label>
                      <Select value={String(parcelamento)} onValueChange={(v) => setParcelamento(parseInt(v) || 1)}>
                        <SelectTrigger id="parcelamento" className={FIELD_CLS} data-testid="select-parcelamento">
                          <SelectValue placeholder="À vista" />
                        </SelectTrigger>
                        <SelectContent>
                          {opcoesDeParcelas(formaPagamento).map((n) => (
                            <SelectItem key={n} value={String(n)}>
                              {n === 1 ? 'À vista (1x)' : `${n}x`}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  {isProposta && formaPagamento === FINANCIAMENTO && (
                    <>
                      <div className="space-y-2">
                        <Label htmlFor="parcelas-financiamento">Número de parcelas *</Label>
                        <Input
                          id="parcelas-financiamento" type="number" min={1} max={120} step={1} required value={parcelamento}
                          onChange={(e) => setParcelamento(e.target.value === '' ? '' : Math.max(1, parseInt(e.target.value, 10) || 1))}
                          className={FIELD_CLS} data-testid="input-parcelas-financiamento"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="valor-parcela">Valor da parcela *</Label>
                        <div className="relative">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm font-medium text-gray-500">R$</span>
                          <Input
                            id="valor-parcela" type="number" min="0.01" step="0.01" required value={valorParcela}
                            onChange={(e) => setValorParcela(e.target.value)}
                            className={`${FIELD_CLS} pl-9`} data-testid="input-valor-parcela"
                          />
                        </div>
                        <p className="text-xs text-slate-500 dark:text-slate-400">A parcela que a financeira simulou, com juros.</p>
                      </div>
                    </>
                  )}
                  {isProposta && (
                    <div className="space-y-2">
                      <Label htmlFor="validade-dias">Validade da proposta (dias)</Label>
                      <Input
                        id="validade-dias" type="number" min={1} max={90} value={validadeDias}
                        onChange={(e) => setValidadeDias(Math.min(90, Math.max(1, parseInt(e.target.value) || 1)))}
                        className={FIELD_CLS} data-testid="input-validade-dias"
                      />
                      <p className="text-xs text-slate-500 dark:text-slate-400">O documento sai com "válida até" contado a partir de hoje.</p>
                    </div>
                  )}
                </div>

                <p className="rounded-lg bg-sheet2 px-4 py-3 text-[13px] text-gray-600 dark:text-gray-300">
                  {isProposta
                    ? (formaPagamento === FINANCIAMENTO
                      ? 'O documento sai com as parcelas e o valor da parcela. A proposta entra na fila de Financiamentos e vira pedido quando a financeira aprovar.'
                      : 'Estas condições saem no documento da proposta. Nada é cobrado até a proposta virar pedido.')
                    : financiamentoPedido
                      ? 'Pedido de financiamento: a forma não muda. A financeira paga a BCMED e o Financeiro registra o recebimento.'
                    : formaPagamento === 'cartao_credito'
                      ? 'O link de pagamento sai na tela de Pedidos. A confirmação é automática pela Pagar.me.'
                      : formaPagamento === 'belluno'
                      ? 'O link Belluno sai na tela de Pedidos: o cliente paga no cartão (até as parcelas acima) e a confirmação é automática.'
                      : 'Depois de criar o pedido, anexe o comprovante na tela de Pedidos. O financeiro confere antes de enviar ao Tiny.'}
                </p>
              </div>
            </AccordionContent>
          </AccordionItem>

          {/* Observations */}
          <AccordionItem value="obs" className="rounded-sheet border border-transparent bg-card shadow-sheet dark:border-border dark:shadow-none overflow-hidden">
            <AccordionTrigger className="px-5 py-4 hover:no-underline transition-colors hover:bg-muted/50">
              <div className="flex items-center gap-3">
                <StepBadge n={6} />
                <h3 className="text-[15px] font-semibold">Observações (opcional)</h3>
              </div>
            </AccordionTrigger>
            <AccordionContent className="px-5 pb-5 pt-0 border-t border-border">
              <div className="pt-4">
                <Textarea
                  value={observacoes}
                  onChange={(e) => setObservacoes(e.target.value)}
                  placeholder="Observações adicionais sobre o pedido..."
                  rows={4}
                  className={FIELD_CLS}
                  data-testid="input-observacoes"
                />
              </div>
            </AccordionContent>
          </AccordionItem>
        </Accordion>

            {/* Resumo (mobile): mesmo card, não-sticky, logo antes do submit */}
            <div className="lg:hidden">
              {renderResumo()}
            </div>
          </div>

          {/* Coluna do resumo (desktop): sticky */}
          <aside className="hidden lg:block lg:w-80 lg:flex-shrink-0">
            <div className="sticky top-6">
              {renderResumo()}
            </div>
          </aside>
        </div>
      </form>
      </PageBody>

      <ConfirmDialog
        open={Boolean(semEstoque)} onOpenChange={(o) => { if (!o && !loading) setSemEstoque(null); }}
        variant="warning" title="Vender sem estoque livre?" loading={loading}
        confirmText={isEditMode ? 'Salvar mesmo assim' : 'Vender mesmo assim'}
        onConfirm={confirmarVendaSemEstoque}
        description={AVISO_VENDA_SEM_ESTOQUE}
        warningList={semEstoque ? linhasDeFalta(semEstoque.faltas) : undefined}
      />
    </div>
  );
};

export default CreateOrder;
